import { getSpecimens, getSpecimenPositions } from '@/api/specimen'
import { getSynonymTFNameCodes } from '@/api/tbd'
import Utils from "@/utils/index";

export const search = {
  namespaced: true,
  state() {
    return {
      query: {
        pageNumber: 1,
        pageSize: 100,
        sortBy: 'catalog_number',
        sortDirection: 'ASC',
        fields: {},
      },

      map: {
        isPolygon: true, // true: 多邊形框選; false: 矩形框選
        vertexes: [], // 多邊形節點座標
        specimenMarkers: [], // 標本定位資料
      },

      totalPages: 0,
    }
  },

  mutations: {
    // 設定搜尋結果共有幾頁
    setTotalPages(state, totalPages) {
      state.totalPages = totalPages
    },

    // 設定頁碼
    setPageNumber(state, pageNumber) {
      state.query.pageNumber = pageNumber
    },

    // 設定一頁的資料數量
    setPageSize(state, pageSize) {
      state.query.pageSize = pageSize
    },

    // 設定排序欄位
    setSortBy(state, sortBy) {
      state.query.sortBy = sortBy
    },

    // 設定排序方向
    setSortDirection(state, sortDirection) {
      state.query.sortDirection = sortDirection
    },

    // 刪除指定欄位
    deleteField(state, fieldKey) {
      delete state.query.fields[fieldKey]
    },

    // 設定搜尋欄位
    setFields(state, fields) {
      const compactFields = {};
      for (const [key, value] of Object.entries(fields)) {
        if (value !== "") {
          compactFields[key] = value;
        }
      }
      state.query.fields = compactFields
    },

    // 清除 simple_search_name 欄位
    deleteSimpleSearchName(state) {
      delete state.query.fields.simple_search_name;
    },

    // 清除搜尋欄位
    resetFields(state) {
      state.query.fields = {}
    },

    // 設定框選方式
    setPolygonMode(state, isPolygon) {
      state.map.isPolygon = isPolygon
    },

    // 新增框選節點
    addVertex(state, vertex) {
      state.map.vertexes.push(vertex)
    },

    // 設定所有框選節點
    setVertexes(state, vertexes) {
      state.map.vertexes = vertexes
    },

    // 清除所有框選節點
    resetVertexes(state) {
      state.map.vertexes = []
    },

    // 將後台取得的標本座標轉換成在地圖上定位所需的標本座標資料
    setSpecimenMarkers(state, specimenPositions) {
      state.map.specimenMarkers = []
      specimenPositions.forEach(element => {
        const marker = {
          specimenId: element.id,
          title: element.revised_taxon_tw,
          catalogNumber: element.catalog_number,
          latlng: [element.gmap_latitude, element.gmap_longitude]
        }
        state.map.specimenMarkers.push(marker)
      });
    },

    // 清除所有標本座標
    resetSpecimenMarkers(state) {
      state.map.specimenMarkers = []
    },
  },

  getters: {
    // 準備搜尋欄位
    async queryFields(state) {
      const queryFields = {};
      for (const [key, value] of Object.entries(state.query.fields)) {
        if (value !== "" && value !== true && value !== false) {
          const fields = ['catalog_number', 'record_number', 'event_year', 'event_month', 'event_day', 'country_code', 'tf_name_code']
          if (fields.includes(key)) {
            queryFields[key] = value;
          } else {
            queryFields[key] = "%" + value + "%"
          }
        } else if (value === true || value === false) {
          queryFields[key] = value;
        }
      }

      // 若從網址帶入 tfnamecode 查詢, 至 TBD 後台取得所有異名的 TF name code 取代 tf_name_code
      if (queryFields.tf_name_code !== undefined) {
        try {
          const response = await getSynonymTFNameCodes(queryFields.tf_name_code);
          queryFields.tf_name_code = response.data
        } catch (error) {
          console.log('Failed to get TF name code of synonym')
          delete queryFields.tf_name_code
        }
      }

      queryFields.page = state.query.pageNumber
      queryFields.pagesize = state.query.pageSize;
      queryFields.sort_by = state.query.sortBy;
      queryFields.sort_direction = state.query.sortDirection;

      // includeImage convert to include_no_img
      queryFields.include_no_img = !queryFields.includeImage;
      delete queryFields.includeImage;

      // 地圖搜尋範圍
      const coordinates = []
      if (!state.map.isPolygon && state.map.vertexes.length >= 2) { // 矩形框選
        // 計算出所有框選節點所形成的矩形範圍以 [lng, lat] 格式加入 coordinates 陣列
        const bounds = Utils.rectangleBounds(state.map.vertexes)
        coordinates.push([bounds[0][1], bounds[0][0]])
        coordinates.push([bounds[1][1], bounds[0][0]])
        coordinates.push([bounds[1][1], bounds[1][0]])
        coordinates.push([bounds[0][1], bounds[1][0]])
        coordinates.push([bounds[0][1], bounds[0][0]])

        // 加上框選範圍資料
        queryFields.polygon = { type: "Polygon", coordinates: [coordinates] }
      } else if (state.map.vertexes.length >= 3) { // 多邊形框選
        // 依序將框選節點以 [lng, lat] 格式加入 coordinates 陣列
        state.map.vertexes.forEach(element => {
          coordinates.push([element.lng, element.lat])
        });

        // 最後再加上第一個節點
        coordinates.push([state.map.vertexes[0].lng, state.map.vertexes[0].lat])

        // 加上框選範圍資料
        queryFields.polygon = { type: "Polygon", coordinates: [coordinates] }
      }

      return queryFields
    },
  },

  actions: {
    // 根據搜尋欄位取得分頁標本資料
    async getSpecimens({ commit, getters, rootState }) {
      const queryFields = await getters.queryFields

      return getSpecimens(queryFields, !!rootState.user.token).then(
        response => {
          return Promise.resolve(response)
        },
        error => {
          return Promise.reject(error)
        }
      )
    },

    // 根據搜尋欄位取得數量 counts 的標本位置
    async getSpecimenPositions({ commit, getters, rootState }, counts) {
      const queryFields = await getters.queryFields
      queryFields.pagesize = counts

      return getSpecimenPositions(queryFields, !!rootState.user.token).then(
        response => {
          commit('setSpecimenMarkers', response.data)
          return Promise.resolve(response)
        },
        error => {
          return Promise.reject(error)
        }
      )
    }
  },
}
